import { useState } from "react";
import { AppBar, Toolbar, Box, Button, IconButton, Menu, MenuItem } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {Link, matchPath, useLocation, useNavigate} from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from '../assets/images/navbar/logo192.png';
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar({ routesMap, lang }) {
    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // Trova la rotta corrente
    const currentEntry = Object.entries(routesMap).find(([_, def]) =>
        matchPath(def.path[lang], location.pathname)
    );
    const [currentKey, currentDef] = currentEntry || [];

    // Risali al padre se level > 1
    const parentKey = (() => {
        if (!currentDef) return null;
        if (currentDef.level === 1) return currentKey;

        const parent = Object.entries(routesMap).find(
            ([, def]) =>
                def.level === 1 &&
                currentDef.path[lang].startsWith(def.path[lang].replace(/:.*$/, ""))
        );
        return parent ? parent[0] : currentKey;
    })();

    // Menu mobile
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleMenu = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
        <AppBar position="static" color="transparent" elevation={0}>
            <Toolbar>
                {/* Desktop */}
                <Box
                    sx={{
                        display: {xs: "none", md: "flex"},
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {/* Logo a sinistra */}
                    <Box sx={{flex: 1, display: "flex", justifyContent: "flex-start"}}>
                        <Box
                            component="img"
                            src={logo}
                            alt="logo"
                            sx={{maxHeight: "50px", width: "auto", objectFit: "contain"}}
                        />
                    </Box>

                    {/* Menu centrali */}
                    <Box sx={{flex: 1, display: "flex", justifyContent: "center", gap: 2}}>
                        {Object.entries(routesMap)
                            .filter(([key, value]) => value.level === 1)
                            .map(([key, {path}]) => (
                                <Button
                                    color="inherit"
                                    key={key}
                                    component={Link}
                                    to={path[lang]}
                                    variant={parentKey === key ? "contained" : "text"}
                                    sx={{fontSize: { md: "1.0rem", xl: "1.3rem" }, width: { md: "100px", xl: "150px"}, mx: "2px"}}
                                >
                                    {t(`navbar.${key}`)}
                                </Button>
                            ))}
                    </Box>

                    {/* Switch lingua a destra */}
                    <Box sx={{flex: 1, display: "flex", justifyContent: "flex-end"}}>
                        <LanguageSwitcher lang={lang} currentKey={currentKey} variant="desktop" />
                    </Box>
                </Box>

                {/* Mobile */}
                <Box
                    sx={{
                        display: {xs: "flex", md: "none"},
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {/* Colonna sinistra: menu */}
                    <Box sx={{flex: 1, display: "flex", justifyContent: "flex-start"}}>
                        <IconButton onClick={handleMenu} sx={{p: 0}}>
                            <MenuIcon/>
                        </IconButton>
                    </Box>

                    {/* Colonna centrale: logo */}
                    <Box sx={{flex: 1, display: "flex", justifyContent: "center"}}>
                        <Box
                            component="img"
                            src={logo}
                            alt="logo"
                            sx={{height: "40px", width: "auto", objectFit: "contain"}}
                            onClick={() => navigate("/"+lang+"/home")}
                        />
                    </Box>

                    {/* Colonna destra: lingue */}
                    <Box sx={{flex: 1, display: "flex", justifyContent: "flex-end"}}>
                        <LanguageSwitcher lang={lang} currentKey={currentKey} variant="mobile" />
                    </Box>

                    {/* Menu a discesa */}
                    <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                        {Object.entries(routesMap)
                            .filter(([key, value]) => value.level === 1)
                            .map(([key, {path}]) => (
                                <MenuItem
                                    key={key}
                                    component={Link}
                                    to={path[lang]}
                                    onClick={handleClose}
                                >
                                    {t(`navbar.${key}`)}
                                </MenuItem>
                            ))}
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
}